import { useQuery } from '@apollo/client';
import { useUserLocation } from 'Account/hooks';
import { getDefaultPractitionerDateRange } from 'Book/shared/utils';
import { useMemo } from 'react';
import {
  FeaturedPractitioners,
  FeaturedPractitionersVariables,
} from 'Services/BeGateway/Practitioner/__generated__/featuredPractitioners';
import { FEATURED_PRACTITIONERS_QUERY } from 'Services/BeGateway/Practitioner/practitioner.services';
import { BEGatewayQueryContext, defaultQueryConfig } from 'utils/apollo';

interface UseFeaturedPractitionersProps {
  limit: number;
  cacheConfig?: 'no-cache' | 'cache-and-network';
}

export const useFeaturedPractitioners = ({
  limit,
  cacheConfig,
}: UseFeaturedPractitionersProps) => {
  const today = useMemo(() => new Date(), []);
  const defaultDateRange = getDefaultPractitionerDateRange(today);
  const {
    hasValidLocationData: userHasValidLocationData,
    userLocationData,
    loading: loadingLocation,
  } = useUserLocation();
  const userLocation = userLocationData?.user;

  const country = userLocation?.country?.value ?? '';
  const state = userLocation?.state?.value;

  const featuredPractitionersQueryVariables: FeaturedPractitionersVariables = {
    limit,
    country,
    state,
    ...defaultDateRange,
  };

  const { data, loading, error, refetch } = useQuery<FeaturedPractitioners>(
    FEATURED_PRACTITIONERS_QUERY,
    {
      fetchPolicy: cacheConfig ?? 'cache-and-network',
      variables: {
        ...featuredPractitionersQueryVariables,
      },
      skip: !userHasValidLocationData,
      ...BEGatewayQueryContext,
      ...defaultQueryConfig,
    },
  );

  return {
    data,
    loading: loading || loadingLocation,
    error,
    refetch: async () => refetch({ ...featuredPractitionersQueryVariables }),
  };
};
